import React, { ReactNode } from 'react';
import toast, { ToastPosition } from 'react-hot-toast';

type ToastAnnouncementListener = (message: string) => void;

const announcementListeners = new Set<ToastAnnouncementListener>();

export const subscribeToToastAnnouncements = (
  listener: ToastAnnouncementListener
) => {
  announcementListeners.add(listener);

  return () => {
    announcementListeners.delete(listener);
  };
};

const extractText = (node: ReactNode): string => {
  if (node === null || node === undefined || typeof node === 'boolean') {
    return '';
  }
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) return node.map(extractText).join(' ');
  if (React.isValidElement(node)) return extractText(node.props.children);

  return '';
};

type ToastOptions = {
  duration?: number;
  position?: ToastPosition;
  id?: string;
};

const defaultOptions: ToastOptions = {
  // We're never automatically hiding toasts to meet timing adjustable accessibility rules
  // In the future, we can remove this if users have the ability to customise toast settings, including turning them off, adjusting the duration, or extending the time limits
  duration: Infinity,
  position: 'bottom-center',
};

export const createToast = (component: any, options: ToastOptions = {}) => {
  const toastOptions = { ...defaultOptions, ...options };
  toast.custom(
    (toast) =>
      React.cloneElement(component, {
        toastId: toast.id,
        visible: toast.visible,
      }),
    toastOptions
  );

  const message = extractText(component).replace(/\s+/g, ' ').trim();
  if (message) {
    announcementListeners.forEach((listener) => listener(message));
  }
};

export const dismissToast = toast.dismiss;

/**
 * The currently focused element, if any. Call this as the first line of a
 * click handler - before any state update or await - to capture the true
 * trigger element for a Toast's `restoreFocusTo` prop. Capturing it any
 * later risks reading `document.activeElement` after the trigger has
 * already been disabled or unmounted by the handler's own side effects.
 */
export const getFocusedElement = (): HTMLElement | null =>
  document.activeElement instanceof HTMLElement ? document.activeElement : null;
